/** Per job kind: recent successes/failures and the kill switch (setting `sources.<kind>.enabled`). Used on the Jobs
 * page and the Settings "Sources" tab. */
import { useQuery } from "@tanstack/react-query";
import { api, unwrap } from "../../api/client";
import { qk } from "../../api/keys";
import { StatusPill } from "../../components/StatusPill";
import { useAction } from "../lib/actions";
import { ActionError, QueryView, TableScroll } from "../lib/ui";
import { AdminOnlyNote } from "../settings/AdminOnly";
import { useSettings } from "../settings/typed";
import { fmtWhen } from "../lib/time";

export function useSourcesHealth() {
  return useQuery({ queryKey: qk.list("sources.health"), queryFn: () => unwrap(api.GET("/api/sources/health")) });
}

export const killSwitchKey = (kind: string) => `sources.${kind}.enabled`;

/** `canSwitch`: the kill switches write `PATCH /api/settings` (admin scope); without it the column is read-only. */
export function SourceHealthTable({ canSwitch: mayWrite }: { canSwitch: boolean }) {
  const health = useSourcesHealth();
  const settings = useSettings();
  const toggle = useAction(
    (kind: string, enable: boolean) => unwrap(api.PATCH("/api/settings", { body: { [killSwitchKey(kind)]: enable } })),
    {
      label: (kind, enable) => `${enable ? "Enable" : "Disable"} source ${kind}`,
      success: (_r, kind, enable) => `${enable ? "Enabled" : "Disabled"} source ${kind}`,
      invalidate: [qk.list("sources.health"), qk.settings],
    },
  );
  const switchable = new Set((settings.data ?? []).map((s) => s.key));
  return (
    <QueryView q={health} what="source health">
      {(rows) => (
        <>
          <ActionError error={toggle.error} />
          {!mayWrite && <AdminOnlyNote what="the kill switches (Disable / Enable)" testId="sources-admin-only" />}
          <TableScroll label="Source health table">
            <table className="ws7-table" aria-label="Source health" data-testid="source-health">
              <thead>
                <tr>
                  <th scope="col">Kind</th>
                  <th scope="col">Owner</th>
                  <th scope="col">Enabled</th>
                  <th scope="col" className="num">Done 24 h</th>
                  <th scope="col" className="num">Failed 24 h</th>
                  <th scope="col">Last done</th>
                  <th scope="col">Last failed</th>
                  <th scope="col">Last error</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const canSwitch = switchable.has(killSwitchKey(r.kind));
                  return (
                    <tr key={r.kind} data-testid={`source-${r.kind}`}>
                      <td className="ws7-mono">{r.kind}</td>
                      <td>{r.owner}</td>
                      <td>
                        <span className="ws7-inline">
                          <StatusPill state={r.enabled ? "done" : "blocked"} label={r.enabled ? "on" : "off"} labelOnly />
                          {!canSwitch ? (
                            <span className="ws7-muted">no kill switch</span>
                          ) : mayWrite ? (
                            <button type="button" data-testid={`source-toggle-${r.kind}`} disabled={toggle.pending}
                              aria-label={`${r.enabled ? "Disable" : "Enable"} source ${r.kind}`}
                              onClick={() => void toggle.run(r.kind, !r.enabled)}>
                              {r.enabled ? "Disable" : "Enable"}
                            </button>
                          ) : null}
                        </span>
                      </td>
                      <td className="num">{r.done_24h}</td>
                      <td className={`num${r.failed_24h ? " ws7-tone-bad" : ""}`}>{r.failed_24h}</td>
                      <td>{fmtWhen(r.last_done_at)}</td>
                      <td>{fmtWhen(r.last_failed_at)}</td>
                      <td className="ws7-tone-bad">{r.last_error ?? ""}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TableScroll>
        </>
      )}
    </QueryView>
  );
}
